import * as React from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import { AboutShowcaseSection } from './AboutPage';
import { ContactShowcaseSection } from './ContactPage';
import { ServicesShowcaseSection } from './OfferingCertificatesPage';
import { inquiryApi } from '../services/inquiryApi';
import '../styles/MarketingPages.css';

const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidMobile = (phone: string): boolean => {
  const phoneFormatRegex = /^\+?[0-9\s\-()]{10,20}$/;
  if (!phoneFormatRegex.test(phone)) return false;
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length >= 10 && digitsOnly.length <= 15 && !/^(\d)\1+$/.test(digitsOnly);
};

const MarketingHomePage: React.FC = () => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [statusMessage, setStatusMessage] = React.useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (statusMessage) {
      setStatusMessage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim();
    const trimmedCompany = formData.companyName.trim();
    const trimmedMessage = formData.message.trim();

    if (!trimmedName) {
      setStatusMessage({ type: 'error', text: 'Please enter your name.' });
      return;
    }

    if (!trimmedEmail) {
      setStatusMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setStatusMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    if (!trimmedPhone) {
      setStatusMessage({ type: 'error', text: 'Please enter your contact / mobile number.' });
      return;
    }

    if (!isValidMobile(trimmedPhone)) {
      setStatusMessage({
        type: 'error',
        text: 'Please enter a valid mobile number (10 to 15 digits).',
      });
      return;
    }

    const finalMessage = trimmedMessage || 'Request for a free sample of ISO 15378:2017 Certification';

    setIsSubmitting(true);
    setStatusMessage(null);

    const result = await inquiryApi.submitInquiry({
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      companyName: trimmedCompany || undefined,
      message: finalMessage,
    });

    setIsSubmitting(false);

    if (result.success) {
      setStatusMessage({
        type: 'success',
        text: 'Thank you! Your sample request has been submitted successfully. Our team will contact you soon.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        companyName: '',
        message: '',
      });
    } else {
      setStatusMessage({
        type: 'error',
        text: result.error || 'Failed to submit your request. Please try again.',
      });
    }
  };

  return (
    <div className="marketing-page">
      <section id="home" className="marketing-hero hero-gradient">
        <div className="hero-topbar" aria-label="CertificationISO contact summary">
          <span>info@certificationiso.com</span>
          <span>info@certificationiso.com</span>
          <span>5/346, Sector-5, Jankipuram Ext. Lucknow - 226031</span>
        </div>

        <div className="marketing-hero-copy">
          <BrandLogo compact />
          <span className="eyebrow-pill">Certification advisory partner</span>
          <h1>
            <span className="hero-accent">ISO</span> 15378:2017
          </h1>
          <p>
            Practical support for documentation, audit preparation, and recognized certification
            programs across quality, safety, food, medical, and product compliance.
          </p>
          <div className="hero-actions">
            <Link to="/#services" className="btn btn-primary btn-large">
              View Services
            </Link>
            <Link to="/#contact" className="btn btn-secondary btn-large">
              Know More
            </Link>
          </div>
          <div className="hero-offering-columns" aria-label="Top certificate offerings">
            <ul>
              <li>ISO 9001:2015</li>
              <li>ISO 27001:2013</li>
              <li>ISO 29990:2010</li>
              <li>ISO 22000:2018</li>
              <li>ISO 20000-1:2018</li>
              <li>ISO 13485:2016</li>
              <li>ISO 45001:2018</li>
            </ul>
            <ul>
              <li>ISO 14001:2015</li>
              <li>ISO 22716:2007</li>
              <li>ISO 15189:2012</li>
              <li>ISO 15378:2017</li>
              <li>ISO 8000:2014</li>
              <li>ISO 50001:2011</li>
              <li>GMP Compliance</li>
            </ul>
            <ul>
              <li>CE Marking</li>
              <li>FDA Compliance</li>
              <li>RoHS Compliance</li>
              <li>BIFMA Compliance</li>
              <li>HACCP Compliance</li>
              <li>Kosher Compliance</li>
              <li>Organic Compliance</li>
            </ul>
          </div>
        </div>

        <div className="marketing-hero-panel request-panel">
          <div className="panel-card request-card request-card-reference">
            <h2>Get A Free Sample Of ISO 15378:2017 Certification</h2>
            <form
              className="request-form request-form-reference"
              onSubmit={handleSubmit}
              noValidate
              aria-busy={isSubmitting}
            >
              {statusMessage && (
                <div
                  className={`alert ${statusMessage.type === 'success' ? 'alert-success' : 'alert-error'}`}
                  role="alert"
                >
                  {statusMessage.text}
                </div>
              )}
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                aria-label="Your Name"
                autoComplete="name"
                disabled={isSubmitting}
                required
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                aria-label="Your Email"
                autoComplete="email"
                disabled={isSubmitting}
                required
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your Contact No."
                aria-label="Your Contact Number"
                autoComplete="tel"
                disabled={isSubmitting}
                required
              />
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Company Name"
                aria-label="Company Name"
                autoComplete="organization"
                disabled={isSubmitting}
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={2}
                placeholder="Your Message"
                aria-label="Your Message"
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className="btn btn-primary btn-large request-submit request-submit-reference"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <AboutShowcaseSection id="about" />

      <ServicesShowcaseSection id="services" />

      <ContactShowcaseSection id="contact" />
    </div>
  );
};

export default MarketingHomePage;

